import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import Title, { TitleInner } from '../primitive/Title';
import { Label } from '../primitive/Label';

interface Props {
    title: string;
    label?: string;
    icon?: ReactNode;
}

const AttributeTile: FC<React.PropsWithChildren<Props>> = ({ title, label, icon }) => (
    <Inner>
        <Content>
            {icon && <IconContainer>{icon}</IconContainer>}
            <TextContainer>
                <Title variant="small">{title}</Title>
                {label && <StyledLabel variant="small">{label}</StyledLabel>}
            </TextContainer>
        </Content>
    </Inner>
);

const IconContainer = styled.div`
    margin-right: 1.6rem;

    @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        flex: 1;
        margin-right: 0.8rem;
    }

    g path {
        /* TODO: Set back to 1.7 if we will use the SVG imports again. */
        stroke-width: 0.525;
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;

    @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        justify-content: center;
        align-items: center;
        flex: 1;
    }
`;

const StyledLabel = styled(Label)`
    margin: 0.2rem 0;

    font-size: 1rem;
    color: ${({ theme }) => theme.colors.neutral['40']};
    letter-spacing: 0.1rem;
    line-height: 2rem;
    font-weight: 700;

    @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        text-align: center;
    }
`;

const Content = styled.div`
    display: flex;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        display: flex;
        flex-direction: column;

        max-width: none;
    }
`;

const Inner = styled.div`
    width: 100%;
    text-align: left;
    position: relative;
    height: 0;

    @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        padding-top: 123%;
        box-shadow: 0 16px 24px 0 rgba(59, 118, 160, 0.03), 0 24px 80px 0 rgba(59, 118, 160, 0.05);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        text-align: center;
    }

    ${TitleInner} {
        font-weight: 200;
    }

    ${Label} {
        font-family: Manrope, sans-serif;
        text-transform: uppercase;
        color: ${({ theme }) => theme.colors.neutral['30']};
    }

    ${IconContainer} img {
        display: inline-block;
        color: ${({ theme }) => theme.colors.neutral['70']};
        width: 3rem;
        height: 3rem;

        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
            width: 4rem;
            height: 4rem;
        }

        @media (min-width: ${({ theme }) => theme.mediaQueries.m}) {
            width: 5rem;
            height: 5rem;
        }

        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
            width: 6rem;
            height: 6rem;
        }

        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
            width: 7rem;
            height: 7rem;
        }
    }

    /* if I'm the child of a grid that only consists of a single, or 2 children */
    :first-child:last-child,
    :last-child:nth-child(2),
    :nth-last-child(2):first-child {
        ${TitleInner} {
            line-height: 3rem;
        }
    }
`;

export default AttributeTile;
